import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification } from 'sonata-react-components';

import { toId } from '../grouping/domain/groupIds';
import { useGrouping } from '../grouping/hooks/useGrouping';
import { useMultiFlightMap } from '../MultiFlightMap.context';
import { downloadTable } from './fileDownload.helper';
import { buildListExport, getAllItemIds, getScopeIds, includesBrandAndCategory } from './listExport.helper';
import { fetchPoiInfo } from './poiInfo.helper';
import { useExportSizes } from './useExportSizes';

import type { ListType } from '../multiFlightMap.types';
import type { ExportFormat, GroupNameResolver } from './export.types';
import type { ExportScope, ListExportData } from './listExport.helper';
import type { ExportSizeSource, ExportSizes } from './useExportSizes';

export type UseListDownloadResult = {
  /** Number of items of each scope: the selected ones and every (non deleted) one of the list. */
  counts: Record<ExportScope, number>;
  /** Downloads the items of the scope (the selected ones by default); warns about the ones that cannot be exported. */
  download: (format: ExportFormat, scope?: ExportScope) => Promise<void>;
  isDownloading: boolean;
  /** Estimated file sizes per scope and format; only calculated while `estimateSizes` is true. */
  sizes: ExportSizes;
};

type Options = {
  /** Turn on (e.g. while the download menu is open) to estimate the file sizes. */
  estimateSizes?: boolean;
};

/** Download of the selected or all the items of one of the map lists (screens, locations or venues) as CSV or Excel. */
export function useListDownload(list: ListType, { estimateSizes = false }: Options = {}): UseListDownloadResult {
  const { formatMessage } = useIntl();
  const { activeSubsection, fullShapes, selectedItems, temporaryScreens, temporaryVenues } = useMultiFlightMap();
  const { groups } = useGrouping(list);
  const [isDownloading, setIsDownloading] = useState(false);

  const data: ListExportData = useMemo(
    () => ({ activeSubsection, fullShapes, selectedItems, temporaryScreens, temporaryVenues }),
    [activeSubsection, fullShapes, selectedItems, temporaryScreens, temporaryVenues]
  );

  const groupNames: GroupNameResolver = useMemo(() => {
    const namesById = new Map(groups.map(group => [group.id, group.name]));
    return groupIds =>
      [...new Set((groupIds ?? []).map(toId))].flatMap(id => (namesById.has(id) ? [namesById.get(id)!] : []));
  }, [groups]);

  const counts = useMemo(
    () => ({ all: getAllItemIds(list, data).length, selected: selectedItems[list]?.length ?? 0 }),
    [data, list, selectedItems]
  );

  // The tables are only built (lazily) the first time the estimate is asked for, then reused until the data change
  const getSizeSources = useMemo(() => {
    let sources: Partial<Record<ExportScope, ExportSizeSource>> | undefined;
    return () => {
      sources ??= Object.fromEntries(
        (counts.selected > 0 ? (['selected', 'all'] as const) : (['all'] as const)).map(scope => {
          const { exported, fileName } = buildListExport(list, getScopeIds(list, scope, data), data, groupNames);
          return [scope, { fileName, table: exported.table }];
        })
      );
      return sources;
    };
  }, [counts.selected, data, groupNames, list]);
  const sizes = useExportSizes(estimateSizes ? getSizeSources() : null);

  const buildExport = useCallback(
    async (scope: ExportScope) => {
      const ids = getScopeIds(list, scope, data);
      const poiInfo = includesBrandAndCategory(list, data)
        ? await fetchPoiInfo(temporaryVenues.filter(venue => !venue.deleted && ids.includes(String(venue._id))))
        : undefined;
      return buildListExport(list, ids, data, groupNames, poiInfo);
    },
    [data, groupNames, list, temporaryVenues]
  );

  const notifyExcluded = useCallback(
    (excludedCount: number, hasFile: boolean) => {
      showNotification(
        'map',
        hasFile
          ? formatMessage(
              {
                defaultMessage:
                  '{count, plural, one {# custom polygon was} other {# custom polygons were}} not downloaded: custom polygons have no coordinates or radius to export.',
                id: 'multiFlightListsMap.actionButtons.download.notification.excluded'
              },
              { count: excludedCount }
            )
          : formatMessage(
              {
                defaultMessage: 'Custom polygons cannot be downloaded ({count}). No file has been generated.',
                id: 'multiFlightListsMap.actionButtons.download.notification.allExcluded'
              },
              { count: excludedCount }
            )
      );
    },
    [formatMessage]
  );

  const download = useCallback(
    async (format: ExportFormat, scope: ExportScope = 'selected') => {
      setIsDownloading(true);
      try {
        const { exported, fileName } = await buildExport(scope);
        const hasFile = exported.table.rows.length > 0;
        if (hasFile) {
          await downloadTable(exported.table, format, fileName);
        }
        if (exported.excludedCount > 0) {
          notifyExcluded(exported.excludedCount, hasFile);
        }
      } catch {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'There was an error generating the file. Please try again later.',
            id: 'multiFlightListsMap.actionButtons.download.notification.error'
          })
        );
      } finally {
        setIsDownloading(false);
      }
    },
    [buildExport, formatMessage, notifyExcluded]
  );

  return { counts, download, isDownloading, sizes };
}
